/**
 * ──────────────────────────────────────────────────────────────────────────
 *  PHASES — the site is "alive" and changes with the time of day.
 * ──────────────────────────────────────────────────────────────────────────
 *
 *  Each phase owns:
 *    • a time window (`from`–`to`, 24h, wraps past midnight)
 *    • a `scene` rendered behind the content (clouds / gears / stars)
 *    • a `celestial` body shown behind the avatar (sun / moon)
 *    • a full set of palette `tokens` — the whole page morphs between them.
 *
 *  Tune the feeling of the site by editing ONLY this file:
 *    • shift when night starts ............ change `from`/`to`
 *    • recolour a phase ................... edit its `tokens`
 *    • give a phase a different backdrop .. change `scene` / `celestial`
 *    • add a phase (e.g. "golden hour") ... push a new object (cycle + auto
 *      detection pick it up automatically)
 *
 *  Palette tokens map 1:1 to the CSS custom properties registered in
 *  styles/timercard.css. They are `@property <color>` so changes animate.
 */

import type { Localized } from "@/lib/i18n";

export type Scene = "clouds" | "gears" | "stars";
export type Celestial = "sun" | "moon";

export interface Phase {
  id: string;
  label: Localized;
  /** Inclusive start hour, exclusive end hour (24h). Wraps if from > to. */
  from: number;
  to: number;
  scene: Scene;
  celestial: Celestial;
  /** Tone of the celestial body, drives sun warmth / moon coolness. */
  celestialTone: string;
  /** CSS custom-property overrides applied to the root. */
  tokens: Record<string, string>;
}

export const PHASES: Phase[] = [
  {
    id: "morning",
    label: { pt: "manhã", en: "morning" },
    from: 5,
    to: 11,
    scene: "clouds",
    celestial: "sun",
    celestialTone: "#ffd98a",
    tokens: {
      "--tc-bg-1": "#eaf7fe",
      "--tc-bg-2": "#cdeafb",
      "--tc-bg-3": "#a9d8f2",
      "--tc-paper": "#e9f4fc",
      "--tc-paper-2": "#d6e9f6",
      "--tc-ink": "#1d3a4f",
      "--tc-ink-strong": "#102a3c",
      "--tc-text": "#244355",
      "--tc-muted": "#345566",
      "--tc-soft": "#3f5f72",
      "--tc-faint": "#4a7186",
      "--tc-brass": "#e0913f",
      "--tc-line": "#102a3c1a",
      "--tc-line-2": "#102a3c26",
      "--tc-line-3": "#102a3c33",
      "--tc-brass-line": "#e0913f80",
    },
  },
  {
    id: "afternoon",
    label: { pt: "tarde", en: "afternoon" },
    from: 11,
    to: 17,
    scene: "gears",
    celestial: "sun",
    celestialTone: "#f2c14e",
    tokens: {
      "--tc-bg-1": "#faf4e6",
      "--tc-bg-2": "#f1e9d8",
      "--tc-bg-3": "#e8ddc5",
      "--tc-paper": "#f7f0df",
      "--tc-paper-2": "#ece2cc",
      "--tc-ink": "#2e2820",
      "--tc-ink-strong": "#241f18",
      "--tc-text": "#473d31",
      "--tc-muted": "#5c5042",
      "--tc-soft": "#6b5f4f",
      "--tc-faint": "#8a7c68",
      "--tc-brass": "#b07a2e",
      "--tc-line": "#2e28201a",
      "--tc-line-2": "#2e282024",
      "--tc-line-3": "#2e28202e",
      "--tc-brass-line": "#b07a2e73",
    },
  },
  {
    id: "dusk",
    label: { pt: "entardecer", en: "dusk" },
    from: 17,
    to: 20,
    scene: "gears",
    celestial: "sun",
    celestialTone: "#ff8a47",
    tokens: {
      "--tc-bg-1": "#ffe6bf",
      "--tc-bg-2": "#f0a268",
      "--tc-bg-3": "#5f4a7e",
      "--tc-paper": "#fbdcc0",
      "--tc-paper-2": "#e9bb9c",
      "--tc-ink": "#2a1622",
      "--tc-ink-strong": "#1c0c16",
      "--tc-text": "#3a2230",
      "--tc-muted": "#56303f",
      "--tc-soft": "#623847",
      "--tc-faint": "#6b3f4f",
      "--tc-brass": "#b03a12",
      "--tc-line": "#1c0c1626",
      "--tc-line-2": "#1c0c1638",
      "--tc-line-3": "#1c0c1647",
      "--tc-brass-line": "#b03a1299",
    },
  },
  {
    id: "night",
    label: { pt: "noite", en: "night" },
    from: 20,
    to: 5,
    scene: "stars",
    celestial: "moon",
    celestialTone: "#e7ecf7",
    tokens: {
      "--tc-bg-1": "#1b2742",
      "--tc-bg-2": "#121b32",
      "--tc-bg-3": "#090f1e",
      "--tc-paper": "#1e2a47",
      "--tc-paper-2": "#283655",
      "--tc-ink": "#c9d3e6",
      "--tc-ink-strong": "#eef2fb",
      "--tc-text": "#aab6cd",
      "--tc-muted": "#8c98b1",
      "--tc-soft": "#9aa6bd",
      "--tc-faint": "#828ea8",
      "--tc-brass": "#d8c074",
      "--tc-line": "#c9d3e61f",
      "--tc-line-2": "#c9d3e62b",
      "--tc-line-3": "#c9d3e63b",
      "--tc-brass-line": "#d8c07480",
    },
  },
];

/** Resolve the phase for a given hour (handles windows that wrap midnight). */
export function phaseForHour(hour: number): Phase {
  const found = PHASES.find((p) =>
    p.from <= p.to ? hour >= p.from && hour < p.to : hour >= p.from || hour < p.to,
  );
  return found ?? PHASES[0]!;
}
